import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
    CheckCircleIcon,
    XCircleIcon,
    UserIcon,
    BuildingOfficeIcon,
    CalendarIcon,
    ExclamationTriangleIcon,
    BriefcaseIcon
} from '@heroicons/react/24/outline';
import { toast } from 'sonner';
import { API_CONFIG } from '../../config/api';
import { getErrorMessage } from '../../utils/errorHelpers';

interface ItemApprovalData {
    approval: {
        id: number;
        approverEmail: string;
        status: string;
        tokenExpiresAt: string;
    };
    item: {
        id: number;
        itemName: string;
        description?: string;
    };
    employee: {
        fullName: string;
        employeeNumber: string;
        designation: string;
        location: string;
        department: string;
    };
    request: {
        id: number;
        requestType: string;
        submittedDate: string;
    };
}

export default function ItemApprovalReview() {
    const [searchParams] = useSearchParams();
    const token = searchParams.get('token');

    const [loading, setLoading] = useState(true);
    const [data, setData] = useState<ItemApprovalData | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [submitting, setSubmitting] = useState(false);
    const [showRejectForm, setShowRejectForm] = useState(false);
    const [rejectionReason, setRejectionReason] = useState('');
    const [successMessage, setSuccessMessage] = useState<string | null>(null);
    const [actionTaken, setActionTaken] = useState<'approved' | 'rejected' | null>(null);

    useEffect(() => {
        const fetchApprovalData = async () => {
            if (!token) {
                setError('No approval token provided');
                setLoading(false);
                return;
            }

            try {
                const response = await fetch(`${API_CONFIG.baseUrl}/api/approvals/review-item?token=${encodeURIComponent(token)}`);

                if (!response.ok) {
                    const errorData = await response.json();
                    throw new Error(errorData.message || 'Failed to load approval request');
                }

                const result = await response.json();
                setData(result);
            } catch (err: unknown) {
                setError(getErrorMessage(err, 'Failed to load approval request'));
                toast.error(getErrorMessage(err, 'Failed to load approval request'));
            } finally {
                setLoading(false);
            }
        };

        fetchApprovalData();
    }, [token]);

    const handleApprove = async () => {
        if (!window.confirm('Are you sure you want to approve this item?')) {
            return;
        }

        setSubmitting(true);
        try {
            const response = await fetch(`${API_CONFIG.baseUrl}/api/approvals/process-item`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    token,
                    action: 'approve'
                }),
            });

            const result = await response.json();

            if (!response.ok || !result.success) {
                throw new Error(result.message || 'Failed to approve item');
            }

            setSuccessMessage(result.message || 'Item approved successfully');
            setActionTaken('approved');
        } catch (err: unknown) {
            toast.error(getErrorMessage(err, 'Failed to approve item'));
        } finally {
            setSubmitting(false);
        }
    };

    const handleReject = async () => {
        if (!rejectionReason.trim() || rejectionReason.trim().length < 10) {
            toast.error('Please provide a rejection reason of at least 10 characters');
            return;
        }

        if (!window.confirm('Are you sure you want to reject this item?')) {
            return;
        }

        setSubmitting(true);
        try {
            const response = await fetch(`${API_CONFIG.baseUrl}/api/approvals/process-item`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    token,
                    action: 'reject',
                    rejectionReason: rejectionReason.trim()
                }),
            });

            const result = await response.json();

            if (!response.ok || !result.success) {
                throw new Error(result.message || 'Failed to reject item');
            }

            setSuccessMessage(result.message || 'Item rejected successfully');
            setActionTaken('rejected');
        } catch (err: unknown) {
            toast.error(getErrorMessage(err, 'Failed to reject item'));
        } finally {
            setSubmitting(false);
        }
    };

    if (loading) {
        return (
            <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center p-4">
                <div className="bg-white rounded-2xl shadow-xl p-8">
                    <div className="flex flex-col items-center">
                        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
                        <p className="mt-4 text-gray-600">Loading approval request...</p>
                    </div>
                </div>
            </div>
        );
    }

    if (error || !data) {
        return (
            <div className="min-h-screen bg-gradient-to-br from-red-50 to-pink-100 flex items-center justify-center p-4">
                <div className="bg-white rounded-2xl shadow-xl p-8 max-w-md w-full text-center">
                    <XCircleIcon className="h-16 w-16 text-red-500 mx-auto mb-4" />
                    <h1 className="text-2xl font-bold text-gray-900 mb-2">Unable to Process Request</h1>
                    <p className="text-gray-600">{error || 'Approval request not found'}</p>
                </div>
            </div>
        );
    }

    if (successMessage && actionTaken) {
        return (
            <div className="min-h-screen bg-gradient-to-br from-green-50 to-emerald-100 flex items-center justify-center p-4">
                <div className="bg-white rounded-2xl shadow-xl p-8 max-w-md w-full text-center">
                    {actionTaken === 'approved' ? (
                        <>
                            <CheckCircleIcon className="h-20 w-20 text-green-500 mx-auto mb-4" />
                            <h1 className="text-3xl font-bold text-gray-900 mb-2">Approved!</h1>
                        </>
                    ) : (
                        <>
                            <XCircleIcon className="h-20 w-20 text-red-500 mx-auto mb-4" />
                            <h1 className="text-3xl font-bold text-gray-900 mb-2">Rejected</h1>
                        </>
                    )}
                    <p className="text-gray-700 text-lg mb-4">{successMessage}</p>
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mt-6">
                        <p className="text-sm text-blue-900">
                            Your decision has been recorded and the relevant parties have been notified.
                            You may now close this page.
                        </p>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 py-8 px-4">
            <div className="max-w-3xl mx-auto">
                {/* Header */}
                <div className="bg-white rounded-2xl shadow-xl p-6 sm:p-8 mb-6">
                    <div className="text-center">
                        <h1 className="text-3xl font-bold text-gray-900 mb-2">📋 Item Approval Request</h1>
                        <span className="inline-block bg-orange-100 text-orange-800 px-4 py-2 rounded-full text-sm font-semibold">
                            Task-Level Approval Required
                        </span>
                    </div>
                </div>

                {/* Item Information */}
                <div className="bg-white rounded-2xl shadow-xl p-6 sm:p-8 mb-6">
                    <h2 className="text-xl font-bold text-gray-900 mb-6">📌 Task Details</h2>
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
                        <h3 className="text-lg font-semibold text-gray-900 mb-2">{data.item.itemName}</h3>
                        {data.item.description && (
                            <p className="text-gray-700">{data.item.description}</p>
                        )}
                    </div>
                </div>

                {/* Employee Information */}
                <div className="bg-white rounded-2xl shadow-xl p-6 sm:p-8 mb-6">
                    <h2 className="text-xl font-bold text-gray-900 mb-6 flex items-center">
                        <UserIcon className="h-6 w-6 mr-2 text-blue-600" />
                        Employee Information
                    </h2>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="flex items-start">
                            <UserIcon className="h-5 w-5 text-gray-400 mr-2 mt-0.5" />
                            <div>
                                <label className="block text-sm font-medium text-gray-500 mb-1">Full Name</label>
                                <p className="text-base font-semibold text-gray-900">{data.employee.fullName}</p>
                            </div>
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-500 mb-1">Employee Number</label>
                            <p className="text-base text-gray-900">{data.employee.employeeNumber}</p>
                        </div>
                        <div className="flex items-start">
                            <BriefcaseIcon className="h-5 w-5 text-gray-400 mr-2 mt-0.5" />
                            <div>
                                <label className="block text-sm font-medium text-gray-500 mb-1">Position</label>
                                <p className="text-base text-gray-900">{data.employee.designation}</p>
                            </div>
                        </div>
                        <div className="flex items-start">
                            <BuildingOfficeIcon className="h-5 w-5 text-gray-400 mr-2 mt-0.5" />
                            <div>
                                <label className="block text-sm font-medium text-gray-500 mb-1">Department</label>
                                <p className="text-base text-gray-900">{data.employee.department}</p>
                            </div>
                        </div>
                        <div className="flex items-start">
                            <BuildingOfficeIcon className="h-5 w-5 text-gray-400 mr-2 mt-0.5" />
                            <div>
                                <label className="block text-sm font-medium text-gray-500 mb-1">Location</label>
                                <p className="text-base text-gray-900">{data.employee.location}</p>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Request Details */}
                <div className="bg-white rounded-2xl shadow-xl p-6 sm:p-8 mb-6">
                    <h2 className="text-xl font-bold text-gray-900 mb-6">📄 Request Details</h2>
                    <div className="space-y-3">
                        <div className="flex justify-between">
                            <span className="text-gray-600">Request Type:</span>
                            <span className="font-semibold text-gray-900">{data.request.requestType}</span>
                        </div>
                        <div className="flex justify-between">
                            <span className="text-gray-600">Submitted Date:</span>
                            <span className="font-semibold text-gray-900">
                                {new Date(data.request.submittedDate + 'Z').toLocaleString()}
                            </span>
                        </div>
                    </div>
                </div>

                {/* Warning */}
                <div className="bg-yellow-50 border border-yellow-200 rounded-2xl p-6 mb-6">
                    <div className="flex items-start">
                        <ExclamationTriangleIcon className="h-6 w-6 text-yellow-600 mr-3 flex-shrink-0 mt-0.5" />
                        <div>
                            <h3 className="font-semibold text-yellow-900 mb-1">Task-Level Approval</h3>
                            <p className="text-yellow-800 text-sm">
                                This is an approval for a specific task. Your decision will affect only this individual task item.
                            </p>
                        </div>
                    </div>
                </div>

                {/* Action Buttons */}
                <div className="bg-white rounded-2xl shadow-xl p-6 sm:p-8">
                    <h2 className="text-xl font-bold text-gray-900 mb-4">🎯 Action Required</h2>
                    <p className="text-gray-600 mb-6">Please review and confirm your choice:</p>

                    {!showRejectForm ? (
                        <div className="flex flex-col sm:flex-row gap-4">
                            <button
                                onClick={handleApprove}
                                disabled={submitting}
                                className="flex-1 px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed font-semibold transition-colors flex items-center justify-center"
                            >
                                <CheckCircleIcon className="h-5 w-5 mr-2" />
                                {submitting ? 'Processing...' : 'Approve Task'}
                            </button>
                            <button
                                onClick={() => setShowRejectForm(true)}
                                disabled={submitting}
                                className="flex-1 px-6 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed font-semibold transition-colors flex items-center justify-center"
                            >
                                <XCircleIcon className="h-5 w-5 mr-2" />
                                Reject Task
                            </button>
                        </div>
                    ) : (
                        <div className="space-y-4">
                            <div>
                                <label htmlFor="rejectionReason" className="block text-sm font-medium text-gray-700 mb-2">
                                    Rejection Reason *
                                </label>
                                <textarea
                                    id="rejectionReason"
                                    value={rejectionReason}
                                    onChange={(e) => setRejectionReason(e.target.value)}
                                    placeholder="Please explain why you are rejecting this task..."
                                    rows={4}
                                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent text-gray-900"
                                    minLength={10}
                                    maxLength={1000}
                                    required
                                />
                                <p className="text-sm text-gray-500 mt-1">
                                    Minimum 10 characters required
                                </p>
                            </div>
                            <div className="flex flex-col sm:flex-row gap-3">
                                <button
                                    onClick={handleReject}
                                    disabled={submitting || rejectionReason.trim().length < 10}
                                    className="flex-1 px-6 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed font-semibold transition-colors"
                                >
                                    {submitting ? 'Submitting...' : 'Submit Rejection'}
                                </button>
                                <button
                                    onClick={() => {
                                        setShowRejectForm(false);
                                        setRejectionReason('');
                                    }}
                                    disabled={submitting}
                                    className="flex-1 px-6 py-3 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 disabled:opacity-50 disabled:cursor-not-allowed font-semibold transition-colors"
                                >
                                    Cancel
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}
